import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Bed, Bath, Ruler, Trees, MapPin, Heart } from "lucide-react";

const nfEUR = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

export default function PropertyCard({ property, index = 0 }) {
  const { t, localized } = useLanguage();
  const img = property.images?.[0];
  const title = localized(property.title);

  return (
    <Link
      to={`/properties/${property.slug || property.id}`}
      data-testid={`property-card-${property.reference || property.id}`}
      className="card group block relative bg-white border border-line hover:border-ink transition-colors"
      style={{
        animation: "fadeUp 0.9s ease-out both",
        animationDelay: `${index * 90}ms`,
      }}
    >
      <div className="relative overflow-hidden aspect-[4/3]">
        {img && (
          <img
            src={img}
            alt={property.image_alts?.[0] || title}
            loading="lazy"
            className={`card-img absolute inset-0 w-full h-full object-cover transition-[filter,opacity] duration-500 ${
              property.status === "sold" ? "grayscale-[35%] opacity-90" : ""
            }`}
          />
        )}
        {property.off_market && (
          <div className="absolute top-3 left-3 bg-obsidian text-obsidian-text text-[10px] tracking-widest2 uppercase px-3 py-1">
            Off-Market
          </div>
        )}
        {!property.off_market && property.type && (
          <div className="absolute top-3 left-3 bg-white/90 text-ink text-[10px] tracking-widest2 uppercase px-3 py-1" data-testid={`property-type-badge-${property.type}`}>
            {t(`properties.${property.type === "plot" ? "plot_land" : property.type}`) || property.type}
          </div>
        )}
        {(property.status === "sold" || property.status === "reserved") && (
          <div
            className={`absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1 backdrop-blur-sm border text-[10px] tracking-widest2 uppercase ${
              property.status === "sold"
                ? "bg-black/55 border-white/25 text-white"
                : "bg-white/70 border-ink/15 text-ink"
            }`}
            data-testid={`property-status-${property.status}`}
          >
            <span className={`w-1 h-1 rounded-full ${property.status === "sold" ? "bg-white/80" : "bg-ink/70"}`} />
            {property.status === "sold" ? t("properties.status_sold") : t("properties.status_reserved")}
          </div>
        )}
        <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-3 right-3 text-right text-white font-mono text-sm">
          {property.off_market ? "On request" : nfEUR.format(property.price)}
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-center gap-1.5 text-muted text-xs">
          <MapPin size={12} strokeWidth={1.6} />
          <span className="uppercase tracking-widest2">{property.location}</span>
        </div>
        <h3 className="font-serif text-xl mt-2 leading-tight text-ink group-hover:text-accent transition-colors line-clamp-2 min-h-[3.2em]">
          {title}
        </h3>

        <div className="mt-4 grid grid-cols-2 gap-y-2 gap-x-4 text-sm text-ink border-t border-line pt-4">
          <SpecRow icon={<Bed size={15} strokeWidth={1.6} />} value={property.bedrooms} label={t("properties.bedrooms")} />
          <SpecRow icon={<Bath size={15} strokeWidth={1.6} />} value={property.bathrooms} label={t("properties.bathrooms")} />
          <SpecRow icon={<Ruler size={15} strokeWidth={1.6} />} value={`${property.area_m2}`} label="m²" />
          {property.plot_m2 > 0 && (
            <SpecRow icon={<Trees size={15} strokeWidth={1.6} />} value={`${property.plot_m2}`} label="m² plot" />
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-line flex items-center justify-between">
          <div className="text-xs font-mono text-muted tracking-widest2">
            {t("properties.ref")} {property.reference}
          </div>
          <div className="font-mono text-sm text-ink">
            {property.off_market ? "—" : nfEUR.format(property.price)}
          </div>
        </div>
      </div>
    </Link>
  );
}

function SpecRow({ icon, value, label }) {
  return (
    <div className="flex items-center gap-2 text-ink">
      <span className="text-muted">{icon}</span>
      <span className="font-mono text-[13px] leading-none">{value}</span>
      <span className="text-muted text-[11px] leading-none">{label}</span>
    </div>
  );
}
